/**
 * 63.5 cover by material, the soil crest's wear (GROUND_WEAR; the coordinator's (b), Oct 12): a count of rounds into soil
 * per person-sized cell of a planet's sphere (the point's direction × the planet's radius, floored per person, so the cell
 * does not move as the ground does); soil whose cell has taken GROUND_WEAR.hitsToPass rounds passes them as sand. The
 * bullets bump it and read it; the cover map reads it. Pure, deterministic.
 *
 * `state.groundWear` is a flat array of [cell id, count] pairs sorted by id (ids are whole numbers from the planet and the
 * cell's three coordinates): read by a binary search, and hashed by the per-tick checksum as one array (cached by its
 * identity) — an object keyed by cell sorted and hashed its 1,600 keys every tick (+0.3 ms at 25 a side).
 */
import type { GameState, GroundMaterial, Vec3 } from '../types';
import { GROUND_WEAR } from '../constants/ground';
import { vecNormalize, vecScale, vecSub } from '../math';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { groundMaterial, groundMaterialAt } from './groundMaterial';

const SPAN = 4096, HALF = 2048;   // a coordinate's room (people) either side of the planet's centre — a planet under 2,048 people out

/** The wear cell a ground point is in (a whole number: the planet, then the cell's three coordinates). */
export function wearId(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, point: Vec3): number {
  const planet = state.planets[planetIndex];
  const c = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const q = vecScale(vecNormalize(vecSub(point, planet.position)), planet.radius);
  const i = Math.floor(q.x / c) + HALF, j = Math.floor(q.y / c) + HALF, k = Math.floor(q.z / c) + HALF;
  return ((planetIndex * SPAN + i) * SPAN + j) * SPAN + k;
}

/**
 * A wear cell's planet and the direction to its centre from the planet's centre — `wearId` undone (lane E's look draws the
 * chewed ground there, 63.5). Pure: whole-number division only.
 */
export function wearCellDir(state: Pick<GameState, 'config'>, id: number): { planetIndex: number; dir: Vec3 } {
  const k = id % SPAN; let r = (id - k) / SPAN;
  const j = r % SPAN; r = (r - j) / SPAN;
  const i = r % SPAN, planetIndex = (r - i) / SPAN;
  const c = PERSON_BODY_HEIGHT * personSetOf(state as GameState).k;
  return { planetIndex, dir: vecNormalize({ x: (i - HALF + 0.5) * c, y: (j - HALF + 0.5) * c, z: (k - HALF + 0.5) * c }) };
}

/** Where cell `id` is (or goes) in the sorted pairs: its pair's index (×2). */
function find(wear: readonly number[], id: number): number {
  let lo = 0, hi = wear.length / 2;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (wear[2 * mid] < id) lo = mid + 1; else hi = mid; }
  return lo;
}

/** The rounds cell `id` has taken. */
export function wearCount(wear: readonly number[] | undefined, id: number): number {
  if (!wear || wear.length === 0) return 0;
  const p = find(wear, id);
  return 2 * p < wear.length && wear[2 * p] === id ? wear[2 * p + 1] : 0;
}

/** One more round into cell `id`, in `wear` (the caller's own copy). */
export function bumpWear(wear: number[], id: number): void {
  const p = find(wear, id);
  if (2 * p < wear.length && wear[2 * p] === id) wear[2 * p + 1]++;
  else wear.splice(2 * p, 0, id, 1);
}

/** Has the soil at a ground point taken enough rounds to pass them? */
export function worn(state: Pick<GameState, 'planets' | 'config'> & Partial<Pick<GameState, 'groundWear'>>, planetIndex: number, point: Vec3): boolean {
  return !!state.groundWear?.length && wearCount(state.groundWear, wearId(state, planetIndex, point)) >= GROUND_WEAR.hitsToPass;
}

/** Does a planet have any wear? */
export function wearOn(state: Partial<Pick<GameState, 'groundWear'>>, planetIndex: number): boolean {
  const w = state.groundWear;
  if (!w || w.length === 0) return false;
  const lo = planetIndex * SPAN * SPAN * SPAN, hi = (planetIndex + 1) * SPAN * SPAN * SPAN;
  const p = find(w, lo);
  return 2 * p < w.length && w[2 * p] < hi;
}

/** The ground's material against a round at a point now: worn soil reads as sand (`seconds`: the moment, for rain's mud; omit for the dry ground). */
export function roundMaterial(state: Pick<GameState, 'planets' | 'config'> & Partial<Pick<GameState, 'groundWear' | 'turnNumber'>>, planetIndex: number, point: Vec3, seconds?: number): GroundMaterial {
  const planet = state.planets[planetIndex];
  const dir = vecNormalize(vecSub(point, planet.position));
  const m = seconds === undefined ? groundMaterialAt(planet, dir) : groundMaterial({ ...state, turnNumber: state.turnNumber ?? 0 }, planetIndex, dir, seconds);
  return m === 'soil' && worn(state, planetIndex, point) ? 'sand' : m;
}
